import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SafeStorage } from '../utils/safe-storage';
import { flushDeferredReload, registerUploadGuard, takeReloadReason } from '../utils/reload-app';
import { AnalyticsService } from './analytics.service';

/**
 * What the app-level upload banner renders. Derived from the
 * `uploadProcessingState` snapshot that AddFromGalleryPopupComponent writes on
 * minimize/destroy, refreshed against the upload-progress API.
 */
export interface UploadBannerState {
  eventId: string;
  /** Hex id the event page is keyed on (localStorage 'eventId'); eventId is numeric. */
  eventHex?: string;
  eventName: string;
  /** The host's own event: "album" in the copy rather than "Event Gallery". */
  isHost?: boolean;
  /** Route to send the user back to when they tap the banner. */
  returnUrl: string;
  total: number;
  /** Files whose S3 transfer has finished — the first of the two legs. */
  uploaded: number;
  /** Files the backend has finished processing — the second leg. */
  completed: number;
  failed: number;
  remaining: number;
  percent: number;
  /**
   * 'stopped' = the window that was running the upload is gone and photos never
   * finished. Not an error to retry — nothing is running — but something the
   * person has to be told, because from the outside it looks identical to a
   * slow upload.
   */
  phase: 'uploading' | 'processing' | 'done' | 'stopped';
  /** The person pressed Stop; the photos already on their way are finishing. */
  stopping?: boolean;
  /** On 'done': photos left out by that stop. Included in `failed`. */
  stopped?: number;
}

/** Shape written by AddFromGalleryPopupComponent.minimizeDialog(). */
interface StoredProcessingState {
  eventId?: string;
  eventHex?: string;
  eventName?: string;
  isHost?: boolean;
  returnUrl?: string;
  filesCount?: number;
  uploadedCount?: number;
  completed?: number;
  failed?: number;
  remaining?: number;
  s3Keys?: string[];
  /**
   * Every key of the batch, not just the ones that have started. `s3Keys` only
   * grows as files begin, so a batch that died early would otherwise leave the
   * rest of its tracking rows unreachable.
   */
  allS3Keys?: string[];
  timestamp?: number;
  /** Written once the batch is known to have stopped; lives until dismissed. */
  stopped?: boolean;
  stoppedRemaining?: number;
}

/**
 * Written every few seconds by whichever window is running an upload, and
 * removed when it finishes. A heartbeat that has gone quiet, with no clean
 * finish, is the only direct evidence we can have that the window holding the
 * upload is gone — the upload loop lives in that window's memory and nothing
 * on the server knows it exists.
 */
const HEARTBEAT_KEY = 'uploadEngineHeartbeat';
/**
 * Which browser tab is beating (an id kept in that tab's sessionStorage, which
 * survives a reload of the tab and nothing else). Lets a reloaded tab see that
 * the upload it is still showing was its own, and so is certainly gone.
 */
const HEARTBEAT_TAB_KEY = 'uploadEngineTab';
const TAB_ID_KEY = 'uploadTabId';
/** Asks whether any other open page is running the upload. */
const ENGINE_CHANNEL = 'picaggo-upload-engine';
/** How long a running engine in another page has to answer. */
const ENGINE_PING_MS = 1000;
const HEARTBEAT_MS = 5000;
/** A beat this late means the page was not running: three missed beats. */
const RESUME_GAP_MS = HEARTBEAT_MS * 3;
/**
 * Last visibility change seen while an upload was running: `{ state, at }`.
 * Survives the page, so the next one can tell a reload that followed the page
 * being hidden (the browser discarding it in the background) from one that did not.
 */
const PAGE_STATE_KEY = 'uploadPageState';
/**
 * Silence this long means the engine is not running. Much wider than the beat:
 * Chrome throttles timers in a hidden tab to about one a minute after five
 * minutes, and a slow beat is not a dead one.
 */
const ENGINE_STALE_MS = 150000;
/** A stopped upload is still worth reporting a day later; after that it is old news. */
const STOPPED_NOTICE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const STORAGE_KEY = 'uploadProcessingState';
const POLL_MS = 5000;
/** Matches the dialog's own give-up window; a snapshot older than this is stale. */
const MAX_AGE_MS = 300000;
/**
 * The finished notice. It used to retire itself after four seconds, which is
 * easy to miss entirely when the upload ends while the person is on another
 * screen or another tab. It now stays until they close it or tap it, and
 * survives a reload for this long.
 */
const DONE_KEY = 'uploadDoneNotice';
const DONE_NOTICE_MAX_AGE_MS = 10 * 60 * 1000;
/** Empty poll responses tolerated before the batch is written off (~15s). */
const MAX_EMPTY_POLLS = 3;
/** Poll errors tolerated before saying so out loud. Never expires the batch —
 *  a broken progress endpoint says nothing about whether files are uploading. */
const MAX_SILENT_POLL_FAILURES = 3;

/**
 * Drives the upload banner that lives in AppComponent, outside the router
 * outlet — the single reason it survives navigation. It deliberately owns no
 * upload work: the S3 loop still lives in AddFromGalleryPopupComponent, and
 * this only reflects the state that dialog left behind. That means progress is
 * server-reported (files the API has heard about), not byte-level live, and an
 * upload whose tab is closed is still gone. Moving the engine into a service is
 * the follow-up this experiment is meant to justify or rule out.
 */
@Injectable({ providedIn: 'root' })
export class UploadBannerService {
  private readonly state = new BehaviorSubject<UploadBannerState | null>(null);
  readonly state$: Observable<UploadBannerState | null> = this.state.asObservable();

  /**
   * "The gallery for this event should refresh."
   *
   * A batch that finishes while minimized has no dialog left to run the usual
   * post-upload refresh, so the photos landed and the gallery sat stale.
   *
   * Emitted from two places on purpose. `processing` comes from the poll and is
   * the accurate one. `upload` comes from the upload loop itself, which keeps
   * running inside the destroyed dialog and therefore cannot fail to fire — the
   * safety net for when the progress endpoint never reports completion, which
   * is exactly the case that left the gallery stale.
   */
  private readonly completed = new Subject<{ eventId: string; reason: 'upload' | 'processing' }>();
  readonly completed$: Observable<{ eventId: string; reason: 'upload' | 'processing' }> =
    this.completed.asObservable();

  private pollTimer: any = null;
  private doneTimer: any = null;
  private inFlight = false;
  /** Consecutive polls that came back knowing nothing about this batch. */
  private emptyPolls = 0;
  /** Consecutive polls that threw. Reported once, not every 5 seconds. */
  private failedPolls = 0;

  readonly enabled = environment.features.persistentUploadBanner === true;

  constructor(private http: HttpClient, private analytics: AnalyticsService) {
    // Read before anything in this window starts a beat of its own: what is in
    // storage now was left by the previous page.
    this.reportPageStart();
    registerUploadGuard({
      isLive: () => !!this.engineTimer,
      report: (event, props) => this.analytics.capture(event, props),
    });
    this.listenForEnginePings();
  }

  // --- Same-tab reload: is the upload this page shows its own, now gone? ----

  private readonly tabId = this.readTabId();
  private engineChannel: BroadcastChannel | null = null;
  private ownEngineChecked = false;

  private readTabId(): string {
    try {
      let id = sessionStorage.getItem(TAB_ID_KEY);
      if (!id) {
        id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        sessionStorage.setItem(TAB_ID_KEY, id);
      }
      return id;
    } catch {
      return '';
    }
  }

  /** A page running an upload answers "is anyone uploading?" from any other page. */
  private listenForEnginePings(): void {
    if (!this.enabled || typeof BroadcastChannel === 'undefined') {
      return;
    }
    try {
      this.engineChannel = new BroadcastChannel(ENGINE_CHANNEL);
      this.engineChannel.onmessage = (event: MessageEvent) => {
        if (event.data?.type === 'ping' && this.engineTimer) {
          this.engineChannel?.postMessage({ type: 'pong', id: event.data.id });
        }
      };
    } catch {
      this.engineChannel = null;
    }
  }

  /**
   * True when the upload behind the snapshot was running in this very tab and
   * no open page answers for it: the tab was reloaded (or navigated away and
   * back) and the upload went with the old page.
   *
   * Without this the banner waited for the heartbeat to go quiet for 150 s,
   * showing "Uploading… 17 of 40" over nothing for two and a half minutes. The
   * reload is meant to mark the beat on its way out (pagehide), but that is not
   * reliable. The tab id alone is not proof either: a duplicated tab, or one
   * opened from this one, inherits sessionStorage and so the id. Hence the
   * ping, which a page that is really uploading answers within milliseconds.
   */
  private async ownEngineGone(): Promise<boolean> {
    if (this.engineTimer || !this.tabId || !this.engineChannel) {
      return false;
    }
    if (SafeStorage.getItem(HEARTBEAT_KEY) === null || SafeStorage.getItem(HEARTBEAT_TAB_KEY) !== this.tabId) {
      return false;
    }
    const channel = this.engineChannel;
    const id = `${this.tabId}:${Date.now()}`;
    const answered = await new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => {
        channel.removeEventListener('message', onMessage);
        resolve(false);
      }, ENGINE_PING_MS);
      const onMessage = (event: MessageEvent) => {
        if (event.data?.type === 'pong' && event.data.id === id) {
          clearTimeout(timer);
          channel.removeEventListener('message', onMessage);
          resolve(true);
        }
      };
      channel.addEventListener('message', onMessage);
      channel.postMessage({ type: 'ping', id });
    });
    return !answered && !this.engineTimer;
  }

  /** Once per page: settle a snapshot that this tab's previous page left running. */
  private async checkOwnEngine(stored: StoredProcessingState): Promise<void> {
    if (this.ownEngineChecked) {
      return;
    }
    this.ownEngineChecked = true;
    if (await this.ownEngineGone()) {
      void this.confirmEngineLost(stored, 'same_tab');
    }
  }

  /**
   * Why did this page start? Says so when it was a reload, and whether the page
   * before it died with an upload running. A reload that went through
   * `reloadApp` carries its reason; one that did not is the browser's doing
   * (a discarded background tab, a crash), and `previous_page_state` shows
   * whether the page was hidden at the time.
   */
  private reportPageStart(): void {
    try {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      const navType = nav?.type ?? null;
      const marker = takeReloadReason();
      const { silentMs: beatAge, leftByPagehide } = this.heartbeatForReport();
      const hadBeat = SafeStorage.getItem(HEARTBEAT_KEY) !== null;
      const pageState = this.readPageState();
      SafeStorage.removeItem(PAGE_STATE_KEY);

      if (navType !== 'reload' && !marker) {
        return;
      }
      this.analytics.capture('app_reloaded', {
        reason: marker?.reason ?? 'unexplained',
        explained: !!marker,
        forced: marker?.forced ?? null,
        nav_type: navType,
        upload_was_live: hadBeat,
        heartbeat_age_ms: beatAge,
        left_by_pagehide: leftByPagehide,
        previous_page_state: pageState?.state ?? null,
        previous_state_age_ms: pageState ? Date.now() - pageState.at : null,
        reload_gap_ms: marker ? Date.now() - marker.at : null,
      });
    } catch {
      // Telemetry about a start must never get in the way of one.
    }
  }

  private readPageState(): { state: string; at: number } | null {
    try {
      const raw = SafeStorage.getItem(PAGE_STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed.state === 'string' && Number.isFinite(parsed.at) ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * Re-read the stored snapshot and show/hide accordingly. Called by the dialog
   * right after it writes the snapshot, and by AppComponent on every completed
   * navigation so a banner appears even when the state was written by a
   * component that has since been destroyed.
   */
  refresh(): void {
    if (!this.enabled) {
      return;
    }

    const stored = this.readStored();
    if (!stored) {
      // No live batch. A finished one is still worth showing: this runs on every
      // navigation, and hiding here is what made the "done" strip vanish the
      // moment the person changed screen.
      const done = this.readDone();
      if (done) {
        this.stopTimers();
        this.publish(done);
        this.armDoneExpiry(this.readDoneAt());
        return;
      }
      this.hide();
      return;
    }
    // A new batch replaces the old finished notice.
    SafeStorage.removeItem(DONE_KEY);

    // A batch already known to have stopped is reported as such on every
    // route until the person dismisses it — and not polled, since nothing is
    // running to move it. Unless the engine has turned out to be alive after
    // all (a frozen tab that thawed), in which case the notice was premature.
    if (stored.stopped) {
      if (this.heartbeatFresh()) {
        this.clearStopped(stored);
      } else {
        this.stopTimers();
        this.publishStopped(stored);
        return;
      }
    }

    // The window that was running this upload may be gone. Settle it against
    // the server; the normal path below carries on meanwhile so nothing flashes.
    if (this.engineLost()) {
      void this.confirmEngineLost(stored);
    } else {
      void this.checkOwnEngine(stored);
    }

    // Already showing this batch with numbers the poll has moved on from.
    // Re-seeding from the snapshot here would rewind the banner to its
    // minimize-time counts on every single navigation.
    if (this.state.value && this.state.value.phase !== 'done'
      && this.state.value.eventId === String(stored.eventId ?? '')) {
      this.startPolling(stored);
      return;
    }

    const total = this.resolveTotal(stored);
    // Deliberately NOT falling back to uploadedCount: that is the transfer leg
    // and now has its own field. Treating it as "completed" made a fully
    // uploaded batch seed with remaining 0, i.e. finished, before the first
    // poll could say otherwise.
    const completed = stored.completed || 0;
    const failed = stored.failed || 0;

    // A snapshot that describes no files cannot produce a useful banner — it
    // renders "0 of 0" and reopens into an empty dialog. Historically this was
    // self-sustaining: the empty dialog wrote 0 straight back on the next
    // minimize. Drop it rather than show it.
    if (total === 0) {
      SafeStorage.removeItem(STORAGE_KEY);
      this.hide();
      return;
    }

    // Seed values only; the first poll replaces them with server truth. Opens
    // on the upload leg unless every file is already sent (a batch handed over
    // at Done): that one opens on "getting ready", not five seconds of
    // "Uploading… 55 of 55".
    const allSent = (stored.uploadedCount || 0) >= total;
    this.publish({
      eventId: String(stored.eventId ?? ''),
      eventHex: stored.eventHex,
      eventName: stored.eventName || (stored.isHost ? 'your album' : 'your Event Gallery'),
      isHost: stored.isHost,
      returnUrl: stored.returnUrl || '',
      total,
      uploaded: stored.uploadedCount || 0,
      completed,
      failed,
      remaining: Math.max(total - completed - failed, 0),
      percent: allSent
        ? this.toPercent(completed, failed, total)
        : this.toPercent(stored.uploadedCount || 0, 0, total),
      phase: allSent ? 'processing' : 'uploading'
    });

    this.startPolling(stored);
  }

  /**
   * Live upload-leg progress, reported directly by the dialog's upload loop.
   *
   * That loop keeps running after the dialog is destroyed — it is what actually
   * finishes the transfer — so it is both the most immediate and the most
   * reliable source for how many files have landed. The progress API is a
   * round-trip that can fail silently; leaning on it alone is why the banner
   * could sit motionless while files were demonstrably uploading. The API poll
   * is still what drives the processing leg, which only the server knows about.
   */
  reportUploadProgress(uploaded: number, failed: number, total: number): void {
    if (!this.enabled) {
      return;
    }
    const prev = this.state.value;
    if (!prev || prev.phase === 'done') {
      return;
    }

    const resolvedTotal = Math.max(total || 0, prev.total);
    if (!resolvedTotal) {
      return;
    }

    // Only the transfer leg is being reported here. Once everything has landed,
    // stand aside and let the poll speak for the processing leg rather than
    // declaring the batch finished on the strength of the upload alone.
    const stillUploading = uploaded + failed < resolvedTotal;

    this.publish({
      ...prev,
      total: resolvedTotal,
      uploaded,
      failed: Math.max(failed, prev.failed),
      percent: stillUploading ? this.toPercent(uploaded, 0, resolvedTotal) : prev.percent,
      phase: stillUploading ? 'uploading' : (prev.phase === 'uploading' ? 'processing' : prev.phase)
    });
  }

  /**
   * The transfer leg has finished for a minimized batch. Asks the gallery to
   * refresh without touching the banner, which carries on showing processing.
   */
  notifyUploadFinished(eventId: any, added = 0, failed = 0, total = 0, keys: string[] = [], stopped = 0): void {
    if (!this.enabled) {
      return;
    }
    this.completed.next({ eventId: String(eventId ?? ''), reason: 'upload' });
    this.announceFinished(eventId, added, failed, total, keys, stopped);
  }

  /**
   * Leaves a "finished" notice behind for a batch, whoever finished it. The
   * upload loop calls it, and so does a dialog reopened from the banner: that
   * one used to complete the batch, delete the snapshot and close without a
   * trace, so the strip just vanished. Needs the snapshot, so call it before
   * the snapshot is removed.
   */
  announceFinished(eventId: any, added = 0, failed = 0, total = 0, keys: string[] = [], stopped = 0): void {
    if (!this.enabled) {
      return;
    }

    // The upload loop is the one thing that always knows when the last file
    // landed. The finished notice used to wait for the server to report every
    // file processed, and the snapshot it depends on expires after five
    // minutes, so for a long batch the banner simply disappeared. Show the
    // result now, from the loop's own counts.
    const stored = this.readRaw();
    if (!stored || stored.stopped || (added + failed === 0)) {
      // The person hid the strip, so there is no snapshot and no notice to leave.
      // The gallery still has to learn when the photos are really there: without
      // this the only refresh was the page's 60 second fallback, and a gallery
      // left open showed the old photos.
      if (!stored && added + failed > 0 && keys.length) {
        this.watchProcessing(String(eventId ?? ''), keys);
      }
      return;
    }
    const count = total || this.resolveTotal(stored);
    // Photos only appear in the gallery once the server has processed them, which
    // is after the transfer. Keep watching for that, so the gallery refreshes
    // when they are really there rather than when the last byte landed.
    this.watchProcessing(
      String(stored.eventId ?? eventId ?? ''),
      stored.allS3Keys?.length ? stored.allS3Keys : (stored.s3Keys || [])
    );
    this.showDone({
      eventId: String(stored.eventId ?? eventId ?? ''),
      eventHex: stored.eventHex,
      eventName: stored.eventName || (stored.isHost ? 'your album' : 'your Event Gallery'),
      isHost: stored.isHost,
      returnUrl: stored.returnUrl || '',
      total: count,
      uploaded: added,
      completed: added,
      failed,
      remaining: 0,
      percent: 100,
      phase: 'done',
      stopped,
    });
  }

  private watchGen = 0;

  /**
   * Polls until every file of the batch has been processed (or failed), then
   * tells the gallery to refresh. Independent of the banner: the banner shows
   * "finished" at the end of the transfer, this answers "are the photos there".
   * Gives up after three minutes; the gallery has its own fallback by then.
   */
  private watchProcessing(eventId: string, keys: string[]): void {
    if (!keys.length) {
      return;
    }
    const gen = ++this.watchGen;
    const started = Date.now();
    const tick = async () => {
      if (gen !== this.watchGen || Date.now() - started > 180000) {
        return;
      }
      try {
        const response: any = await this.http
          .post(`${environment.uploadProgressApi}?action=getBulk`, { keys })
          .toPromise();
        const rows: any[] = response?.uploads || [];
        const settled = (u: any) => u.status === 'completed' || u.progress_percentage === 100
          || u.stage === 'completed' || u.status === 'failed' || !!u.error_stage || u.stage === 'failed';
        if (gen === this.watchGen && rows.length && rows.every(settled)) {
          this.completed.next({ eventId, reason: 'processing' });
          return;
        }
      } catch {
        // A failed check says nothing about the files; try again.
      }
      setTimeout(tick, 4000);
    };
    setTimeout(tick, 3000);
  }

  /** Stops all polling, drops the live snapshot, and keeps the result on screen. */
  private showDone(done: UploadBannerState): void {
    this.stopTimers();
    SafeStorage.removeItem(STORAGE_KEY);
    const at = Date.now();
    SafeStorage.setItem(DONE_KEY, JSON.stringify({ at, state: done }));
    this.state.next(done);
    this.armDoneExpiry(at);
    try { navigator.vibrate?.(40); } catch { /* not supported everywhere */ }
    this.flagTitle();
  }

  /**
   * The finished notice used to be checked for age only when the app navigated,
   * so on a screen the person stayed on it simply never left. This retires it
   * on a timer, whatever the person is doing.
   */
  private armDoneExpiry(at: number | null): void {
    if (this.doneTimer) {
      clearTimeout(this.doneTimer);
      this.doneTimer = null;
    }
    if (at === null) {
      return;
    }
    const remaining = Math.max(DONE_NOTICE_MAX_AGE_MS - (Date.now() - at), 0);
    this.doneTimer = setTimeout(() => {
      this.doneTimer = null;
      if (this.state.value?.phase === 'done') {
        SafeStorage.removeItem(DONE_KEY);
        this.restoreTitle();
        this.hide();
      }
    }, remaining);
  }

  private readDoneAt(): number | null {
    try {
      const at = JSON.parse(SafeStorage.getItem(DONE_KEY) || 'null')?.at;
      return Number.isFinite(at) ? at : null;
    } catch {
      return null;
    }
  }

  /**
   * An event was deleted. A notice about its upload points at nothing now, so
   * the finished notice and any snapshot for it go. The banner is only touched
   * when it is about this event; another event's upload carries on.
   */
  eventDeleted(eventId: any): void {
    if (!this.enabled || eventId === null || eventId === undefined || eventId === '') {
      return;
    }
    const id = String(eventId);
    const matches = (s?: { eventId?: any; eventHex?: string } | null) =>
      !!s && (String(s.eventId ?? '') === id || (!!s.eventHex && s.eventHex === id));

    const stored = this.readRaw();
    if (matches(stored)) {
      SafeStorage.removeItem(STORAGE_KEY);
    }
    let doneState: any = null;
    try { doneState = JSON.parse(SafeStorage.getItem(DONE_KEY) || 'null')?.state; } catch { /* ignore */ }
    if (matches(doneState)) {
      SafeStorage.removeItem(DONE_KEY);
    }
    if (matches(this.state.value)) {
      this.restoreTitle();
      this.hide();
    }
  }

  // --- Is anything actually running? ----------------------------------------

  private engineTimer: any = null;
  private pagehideBound = false;
  private lossCheckInFlight = false;
  /** Event whose batch was checked against the server and found intact. */
  private lossVerifiedFor: string | null = null;

  /**
   * Called by the upload loop when it starts. The loop lives in the dialog's
   * closure, so this window is the only place that can say it is still alive.
   * Idempotent: a second batch in the same window shares the beat.
   */
  beginEngineHeartbeat(): void {
    if (!this.enabled || this.engineTimer) {
      return;
    }
    this.lossVerifiedFor = null;
    this.lastBeatAt = null;
    if (this.tabId) {
      SafeStorage.setItem(HEARTBEAT_TAB_KEY, this.tabId);
    }
    this.beat();
    this.engineTimer = setInterval(() => this.beat(), HEARTBEAT_MS);

    // A reload or a closed tab says so before it goes (`persisted` is false —
    // the page is not being kept for a back navigation). Marking the beat
    // ancient at that moment lets the next window say "stopped" straight away
    // instead of after the full silence window. A page that is merely hidden or
    // frozen sends no such signal, which is what the silence window is for.
    if (!this.pagehideBound && typeof window !== 'undefined') {
      this.pagehideBound = true;
      window.addEventListener('pagehide', (event: PageTransitionEvent) => {
        if (this.engineTimer && !event.persisted) {
          SafeStorage.setItem(HEARTBEAT_KEY, '1');
        }
      });
    }

    // Whether the page is hidden while the upload runs is the one thing that
    // separates "the browser suspended or discarded it" from "something
    // reloaded it". The transition is both reported and left in storage for the
    // next page to read.
    if (!this.visibilityBound && typeof document !== 'undefined') {
      this.visibilityBound = true;
      document.addEventListener('visibilitychange', () => this.onVisibilityChange());
    }
    this.notePageState(document.visibilityState === 'hidden' ? 'hidden' : 'visible');
    void this.holdScreenOn();
  }

  private wakeLock: any = null;

  /**
   * Keeps the screen on while this window uploads. The app tells people to keep
   * Picaggo open; a phone that dims and locks on its own timer suspends the page
   * just as switching apps does, to someone who did exactly what they were
   * told. It cannot stop a person locking the phone or leaving the app. The
   * browser drops the lock whenever the page is hidden, so it is taken again
   * on return. Unsupported or refused: the upload carries on regardless.
   */
  private async holdScreenOn(): Promise<void> {
    const wakeLock = (navigator as any)?.wakeLock;
    if (!wakeLock || this.wakeLock || !this.engineTimer || document.visibilityState !== 'visible') {
      return;
    }
    try {
      const lock = await wakeLock.request('screen');
      // The upload may have ended while the request was pending.
      if (!this.engineTimer) {
        void lock.release?.();
        return;
      }
      this.wakeLock = lock;
      lock.addEventListener?.('release', () => {
        if (this.wakeLock === lock) {
          this.wakeLock = null;
        }
      });
    } catch {
      this.wakeLock = null;
    }
  }

  private releaseScreen(): void {
    const lock = this.wakeLock;
    this.wakeLock = null;
    try {
      void lock?.release?.();
    } catch {
      // Already released by the browser.
    }
  }

  private visibilityBound = false;
  private hiddenAt: number | null = null;

  private onVisibilityChange(): void {
    if (!this.engineTimer) {
      return;
    }
    const state = document.visibilityState === 'hidden' ? 'hidden' : 'visible';
    this.notePageState(state);
    if (state === 'hidden') {
      this.hiddenAt = Date.now();
      return;
    }
    this.hiddenAt = null;
    this.checkResumed();
    void this.holdScreenOn();
  }

  /**
   * Did this page stop running for a while? The heartbeat is the witness: a
   * frozen tab runs no code, so its beat simply stops, and the first thing to
   * run after the thaw sees the gap. Timing from the 'hidden' event does not
   * work: Chrome delivers that one late, on thaw, a few ms before 'visible'.
   */
  private checkResumed(): void {
    if (!this.engineTimer || this.lastBeatAt === null) {
      return;
    }
    const gap = Date.now() - this.lastBeatAt;
    if (gap <= RESUME_GAP_MS) {
      return;
    }
    // Consumed, so the next beat does not report the same gap again.
    this.lastBeatAt = Date.now();
    this.analytics.capture('upload_page_resumed', {
      suspended_for_ms: gap,
      event_id: String(this.readRaw()?.eventId ?? ''),
    });
  }

  private notePageState(state: 'hidden' | 'visible'): void {
    SafeStorage.setItem(PAGE_STATE_KEY, JSON.stringify({ state, at: Date.now() }));
  }

  // --- Stopping from the banner ----------------------------------------------

  /**
   * How to stop the upload running in this window. The loop lives in the
   * dialog, which is gone once minimized, so the dialog leaves this behind for
   * the banner. Returns false when there was nothing left to stop.
   */
  private stopHandler: (() => boolean) | null = null;

  registerStop(handler: () => boolean): void {
    this.stopHandler = handler;
  }

  /** True only in the window whose loop is running: another tab's upload can't be stopped from here. */
  get canStop(): boolean {
    return !!this.stopHandler && !!this.engineTimer;
  }

  requestStop(): boolean {
    if (!this.canStop || !this.stopHandler!()) {
      return false;
    }
    const prev = this.state.value;
    if (prev && prev.phase === 'uploading') {
      this.state.next({ ...prev, stopping: true });
    }
    return true;
  }

  // --- One upload at a time per window --------------------------------------

  /**
   * Uploads queued behind the one running here. Two at once shared one
   * heartbeat, one stop handler and one banner snapshot, so the first to finish
   * told the app nothing was uploading while the second still was, and a held
   * reload could then end it. The second one also looked stuck: "0 of 20 ·
   * Calculating…" with no word of why.
   */
  private engineIdleWaiters: Array<() => void> = [];

  /** True while an upload loop is running in this window (needs the banner flag on). */
  get engineRunning(): boolean {
    return !!this.engineTimer;
  }

  /** Calls `start` when this window's upload ends. Returns a cancel. */
  onEngineIdle(start: () => void): () => void {
    if (!this.engineTimer) {
      start();
      return () => { };
    }
    this.engineIdleWaiters.push(start);
    return () => {
      this.engineIdleWaiters = this.engineIdleWaiters.filter(w => w !== start);
    };
  }

  /** Called when the upload loop ends, however it ends. A clean end leaves no beat behind. */
  endEngineHeartbeat(): void {
    this.stopHandler = null;
    if (this.engineTimer) {
      clearInterval(this.engineTimer);
      this.engineTimer = null;
    }
    this.releaseScreen();
    SafeStorage.removeItem(HEARTBEAT_KEY);
    SafeStorage.removeItem(HEARTBEAT_TAB_KEY);
    SafeStorage.removeItem(PAGE_STATE_KEY);
    this.hiddenAt = null;
    this.lastBeatAt = null;
    // A notice that said "stopped" was wrong if the loop finished after all.
    const stored = this.readRaw();
    if (stored?.stopped) {
      SafeStorage.removeItem(STORAGE_KEY);
      this.hide();
    }
    // The next queued upload starts now, and a held reload waits for it too:
    // flushing here would reload the page out from under the photos it was
    // about to send.
    const next = this.engineIdleWaiters.shift();
    if (next) {
      next();
      return;
    }
    // A reload that was held back while this ran can go ahead now.
    flushDeferredReload();
  }

  private lastBeatAt: number | null = null;

  private beat(): void {
    // Either this or the visibility handler runs first after a thaw; both look.
    this.checkResumed();
    this.lastBeatAt = Date.now();
    SafeStorage.setItem(HEARTBEAT_KEY, String(this.lastBeatAt));
  }

  private heartbeatAge(): number | null {
    const raw = SafeStorage.getItem(HEARTBEAT_KEY);
    if (!raw) {
      return null;
    }
    const last = Number(raw);
    return Number.isFinite(last) ? Date.now() - last : null;
  }

  /**
   * The pagehide handler writes '1' to mean "this window went away on purpose",
   * which reads as an age of now-minus-1. Fine for deciding the engine is gone;
   * nonsense as a duration. For telemetry, say so instead.
   */
  private heartbeatForReport(): { silentMs: number | null; leftByPagehide: boolean } {
    if (SafeStorage.getItem(HEARTBEAT_KEY) === '1') {
      return { silentMs: null, leftByPagehide: true };
    }
    return { silentMs: this.heartbeatAge(), leftByPagehide: false };
  }

  private heartbeatFresh(): boolean {
    if (this.engineTimer) {
      return true;
    }
    const age = this.heartbeatAge();
    return age !== null && age <= ENGINE_STALE_MS;
  }

  /**
   * True only when a heartbeat exists, is stale, and this window is not the one
   * beating. No heartbeat at all proves nothing (an older snapshot, or a clean
   * finish), so it is never read as "lost".
   */
  private engineLost(): boolean {
    if (this.engineTimer) {
      return false;
    }
    const age = this.heartbeatAge();
    return age !== null && age > ENGINE_STALE_MS;
  }

  /**
   * Ask the server which of the batch's files never left 'uploading'. Only if
   * some did is anything reported: a stale beat on a batch whose files all
   * landed is a window that closed after finishing, which is fine.
   */
  private async confirmEngineLost(stored: StoredProcessingState, detectedBy: 'silence' | 'same_tab' = 'silence'): Promise<void> {
    const eventId = String(stored.eventId ?? '');
    if (this.lossCheckInFlight || this.lossVerifiedFor === eventId) {
      return;
    }
    const keys = stored.allS3Keys?.length ? stored.allS3Keys : (stored.s3Keys || []);
    if (!keys.length) {
      return;
    }

    this.lossCheckInFlight = true;
    try {
      const response: any = await this.http
        .post(`${environment.uploadProgressApi}?action=getBulk`, { keys })
        .toPromise();
      const rows: any[] = response?.uploads || [];
      const stuck = rows.filter(u => u.status === 'uploading' && !u.error_stage && u.stage !== 'failed');

      if (stuck.length === 0) {
        this.lossVerifiedFor = eventId;
        return;
      }

      const { silentMs, leftByPagehide } = this.heartbeatForReport();
      this.analytics.capture('upload_engine_lost', {
        event_id: eventId,
        unfinished: stuck.length,
        batch_size: Math.max(this.resolveTotal(stored), rows.length),
        silent_for_ms: silentMs,
        left_by_pagehide: leftByPagehide,
        snapshot_age_ms: stored.timestamp ? Date.now() - stored.timestamp : null,
        // 'same_tab': this tab was reloaded and nothing answered for the upload;
        // 'silence': the heartbeat went quiet (or pagehide marked it).
        detected_by: detectedBy,
      });

      this.reportStoppedRows(eventId, stuck.map(u => u.s3_key).filter(Boolean));

      const stoppedState: StoredProcessingState = {
        ...stored,
        stopped: true,
        stoppedRemaining: stuck.length,
        timestamp: Date.now(),
      };
      SafeStorage.setItem(STORAGE_KEY, JSON.stringify(stoppedState));
      this.stopTimers();
      this.publishStopped(stoppedState);
    } catch {
      // The progress endpoint being unreachable is not evidence of anything;
      // try again on the next poll or navigation.
    } finally {
      this.lossCheckInFlight = false;
    }
  }

  /** Correct the rows the dead window left behind. Fire-and-forget by design. */
  private reportStoppedRows(eventId: string, keys: string[]): void {
    if (!environment.features.reportFailedUploads || !keys.length) {
      return;
    }
    this.http
      .post(`${environment.uploadProgressApi}?action=markFailed`, {
        event_id: Number(eventId),
        failures: keys.map(s3_key => ({
          s3_key,
          error_stage: 'client-stopped',
          error_message: 'The page uploading this photo was closed or paused before it finished.',
          retry_count: 0,
        })),
      })
      .subscribe({ next: () => { }, error: () => { } });
  }

  private publishStopped(stored: StoredProcessingState): void {
    const total = this.resolveTotal(stored);
    const remaining = stored.stoppedRemaining ?? Math.max(total - (stored.completed || 0) - (stored.failed || 0), 0);
    this.publish({
      eventId: String(stored.eventId ?? ''),
      eventHex: stored.eventHex,
      eventName: stored.eventName || (stored.isHost ? 'your album' : 'your Event Gallery'),
      isHost: stored.isHost,
      returnUrl: stored.returnUrl || '',
      total,
      uploaded: stored.uploadedCount || 0,
      completed: stored.completed || 0,
      failed: stored.failed || 0,
      remaining,
      percent: this.toPercent(stored.uploadedCount || 0, 0, total),
      phase: 'stopped',
    });
  }

  private clearStopped(stored: StoredProcessingState): void {
    const { stopped, stoppedRemaining, ...rest } = stored;
    SafeStorage.setItem(STORAGE_KEY, JSON.stringify({ ...rest, timestamp: Date.now() }));
    stored.stopped = false;
  }

  /** The stored snapshot without the age check or any side effects. */
  private readRaw(): StoredProcessingState | null {
    const raw = SafeStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /** User dismissed the banner. Drops the snapshot so it does not come back. */
  dismiss(): void {
    SafeStorage.removeItem(STORAGE_KEY);
    SafeStorage.removeItem(DONE_KEY);
    this.restoreTitle();
    this.hide();
  }

  private readDone(): UploadBannerState | null {
    const raw = SafeStorage.getItem(DONE_KEY);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      if (!parsed?.state || Date.now() - (parsed.at || 0) > DONE_NOTICE_MAX_AGE_MS) {
        SafeStorage.removeItem(DONE_KEY);
        return null;
      }
      return parsed.state as UploadBannerState;
    } catch {
      SafeStorage.removeItem(DONE_KEY);
      return null;
    }
  }

  private originalTitle: string | null = null;

  /** Marks the tab so a finished upload is visible from another tab or window. */
  private flagTitle(): void {
    try {
      if (!document.hidden || this.originalTitle !== null) {
        return;
      }
      this.originalTitle = document.title;
      document.title = '✓ Upload finished · ' + this.originalTitle;
      const onVisible = () => {
        if (!document.hidden) {
          document.removeEventListener('visibilitychange', onVisible);
          this.restoreTitle();
        }
      };
      document.addEventListener('visibilitychange', onVisible);
    } catch { /* cosmetic */ }
  }

  private restoreTitle(): void {
    if (this.originalTitle !== null) {
      try { document.title = this.originalTitle; } catch { /* cosmetic */ }
      this.originalTitle = null;
    }
  }

  /**
   * The batch is untrackable. Same teardown as dismiss, but unprompted — the
   * snapshot must go, or the next navigation rebuilds the same stuck banner.
   */
  private expire(): void {
    SafeStorage.removeItem(STORAGE_KEY);
    this.hide();
  }

  /**
   * Clears the banner without touching storage — for when the dialog is
   * reopened and takes ownership of the state again.
   */
  hide(): void {
    this.stopTimers();
    this.emptyPolls = 0;
    if (this.state.value !== null) {
      this.state.next(null);
    }
  }

  private readStored(): StoredProcessingState | null {
    const raw = SafeStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      const parsed: StoredProcessingState = JSON.parse(raw);
      const age = parsed.timestamp ? Date.now() - parsed.timestamp : 0;

      // A "stopped" notice is an answer, not a live batch: it stays until the
      // person dismisses it, up to a day.
      if (parsed.stopped) {
        if (age > STOPPED_NOTICE_MAX_AGE_MS) {
          SafeStorage.removeItem(STORAGE_KEY);
          return null;
        }
        return parsed;
      }

      // A snapshot older than the dialog's own timeout describes an upload
      // nobody is going to hear about again; showing it would pin a permanent
      // "uploading..." strip on every route.
      if (parsed.timestamp && age > MAX_AGE_MS) {
        // Unless the reason nobody will hear about it is that the window
        // running it died. Then the person who comes back deserves to be told,
        // not shown nothing: that silence is what made this look like a slow
        // upload that never finished. The check settles against the server and,
        // if photos really were left behind, replaces the snapshot with a
        // 'stopped' one; either way this call reports no live batch.
        if (age < STOPPED_NOTICE_MAX_AGE_MS && this.engineLost()
          && this.lossVerifiedFor !== String(parsed.eventId ?? '')) {
          void this.confirmEngineLost(parsed);
          return null;
        }
        SafeStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch {
      SafeStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }

  private startPolling(stored: StoredProcessingState): void {
    if (this.pollTimer || !stored.s3Keys?.length) {
      return;
    }
    this.pollTimer = setInterval(() => this.poll(), POLL_MS);
  }

  private async poll(): Promise<void> {
    // Overlapping polls on a slow link would double-count nothing but would
    // stack requests behind a stalled one.
    if (this.inFlight) {
      return;
    }

    const stored = this.readStored();
    if (!stored || !stored.s3Keys?.length) {
      this.hide();
      return;
    }
    // Nothing is running behind a stopped batch, so there is nothing to poll.
    if (stored.stopped) {
      this.stopTimers();
      return;
    }
    // Checked each tick: this is what notices a window that died while the
    // banner was already on screen in another one.
    if (this.engineLost()) {
      void this.confirmEngineLost(stored);
    }

    this.inFlight = true;
    try {
      const response: any = await this.http
        .post(`${environment.uploadProgressApi}?action=getBulk`, { keys: stored.s3Keys })
        .toPromise();

      // A loss check may have settled while this poll was in flight; its answer
      // wins, or the banner would flip back to "uploading" under the notice.
      const afterAwait = this.readRaw();
      if (!afterAwait || afterAwait.stopped) {
        // Stopped, or finished while this poll was in flight (the snapshot is
        // gone): publishing now would overwrite the finished notice.
        return;
      }

      if (!response?.uploads?.length) {
        // initBulk writes a progress row per file before uploading begins, so a
        // successful call with zero rows means this batch no longer exists —
        // not that it is between states. Allow a couple of misses for a flaky
        // endpoint, then stop: a banner pinned at 0% with nothing able to move
        // it is worse than no banner.
        if (++this.emptyPolls >= MAX_EMPTY_POLLS) {
          this.expire();
        }
        return;
      }
      this.emptyPolls = 0;
      this.failedPolls = 0;

      let completed = 0;
      let failed = 0;
      // Files that have finished their S3 transfer, whether or not the backend
      // has processed them yet. Counting only `completed` conflated the two
      // legs: ten files could be fully uploaded and the banner still read
      // "0 of 10", which is indistinguishable from an upload that never
      // started. A row that has left 'uploading' has landed.
      let uploaded = 0;
      response.uploads.forEach((upload: any) => {
        // Same classification the dialog uses in restoreProcessingState — the
        // API reports terminal state across three fields and any of them can
        // be the one that is set.
        const isCompleted = upload.status === 'completed'
          || upload.progress_percentage === 100
          || upload.stage === 'completed';
        const isFailed = upload.status === 'failed' || !!upload.error_stage || upload.stage === 'failed';
        // A row that has completed or failed has necessarily finished its
        // transfer, whatever `status` happens to say. Reading `status` alone
        // here — while completion is recognised across three fields directly
        // above — meant a backend that signals through `stage` or
        // `progress_percentage` and leaves `status` on 'uploading' produced
        // completed > 0 with uploaded == 0. That pins the banner to the
        // uploading phase at 0% for files that have demonstrably landed.
        const hasLanded = isCompleted
          || isFailed
          || (upload.status && upload.status !== 'uploading');
        // Same meaning as the dialog's count: files that made it, so a failed
        // file is reported as failed and never also as uploaded.
        if (hasLanded && !isFailed) {
          uploaded++;
        }
        if (isCompleted) {
          completed++;
        } else if (isFailed) {
          failed++;
        }
      });

      // `filesCount` is the whole batch the user picked; the API only knows
      // about files that have reached it, so total stays anchored to the
      // snapshot and the shortfall reads as "still remaining".
      // The dialog's own total is the number of files picked. Prefer it: the
      // key and row counts can run higher (renamed or retried keys) and made
      // the banner's total differ from the dialog's.
      const total = stored.filesCount
        ? Math.max(stored.filesCount, completed + failed)
        : Math.max(this.resolveTotal(stored), response.uploads.length);
      const remaining = Math.max(total - completed - failed, 0);

      // The dialog's upload loop reports the transfer leg directly and sees a
      // file land before the API hears about it. Letting a poll report fewer
      // landed files than the loop already did bounced the banner out of
      // 'processing' and back to 'uploading' on every five-second tick. Scoped
      // to a batch of the same shape so a stale floor cannot leak into the next
      // upload for this event.
      const prev = this.state.value;
      if (prev && prev.eventId === String(stored.eventId ?? '') && prev.total === total) {
        uploaded = Math.max(uploaded, prev.uploaded);
      }
      uploaded = Math.min(uploaded, total);

      // An upload has two legs and they finish minutes apart. Reporting only
      // the second one is why a batch whose files had all landed still showed
      // 0%: transferring is the part the user can act on (stay on wifi), and
      // processing is the part they only need to know is running.
      const stillUploading = uploaded + failed < total;
      const phase: UploadBannerState['phase'] = remaining === 0
        ? 'done'
        : (stillUploading ? 'uploading' : 'processing');

      this.publish({
        eventId: String(stored.eventId ?? ''),
        eventHex: stored.eventHex,
        eventName: stored.eventName || (stored.isHost ? 'your album' : 'your Event Gallery'),
        isHost: stored.isHost,
        returnUrl: stored.returnUrl || '',
        total,
        uploaded,
        completed,
        failed,
        remaining,
        percent: stillUploading
          ? this.toPercent(uploaded, 0, total)
          : this.toPercent(completed, failed, total),
        phase
      });

      if (remaining === 0) {
        this.finish();
      }
    } catch (err: any) {
      // Keep the last known numbers on screen. A failed progress poll is not
      // evidence the upload failed, and blanking the banner mid-upload is the
      // one thing that would make this worse than no banner at all.
      //
      // But it must not stay invisible either: a silently swallowed failure
      // here is indistinguishable from an upload that is not progressing, and
      // that ambiguity cost real debugging time.
      if (++this.failedPolls === MAX_SILENT_POLL_FAILURES) {
        const detail = err?.status ? `HTTP ${err.status}` : (err?.message || 'unknown');
        // console.warn(`[UploadBanner] progress poll failing (${detail}) //__CONSOLE_LOG__ — banner is showing local counts only`);
        this.analytics.capture('upload_banner_poll_failed', {
          event_id: this.state.value?.eventId,
          error: String(detail).slice(0, 200),
          consecutive_failures: this.failedPolls,
        });
      }
    } finally {
      this.inFlight = false;
    }
  }

  /**
   * Batch is terminal. Deliberately does NOT reload the page — the banner can
   * be on any route by now, and event-detail's old indicator called
   * window.location.reload() here, which would be violent on, say, a
   * half-filled create-event form.
   */
  private finish(): void {
    const done = this.state.value;
    const eventId = done?.eventId ?? '';
    // Keep the result on screen until the person has seen it, on any route and
    // across a reload, rather than a four-second flash.
    if (done) {
      this.showDone(done);
    } else {
      this.stopTimers();
      SafeStorage.removeItem(STORAGE_KEY);
    }

    // Tell the gallery to reload so the new photos are already there when the
    // user looks.
    this.completed.next({ eventId, reason: 'processing' });
  }

  private publish(next: UploadBannerState): void {
    const prev = this.state.value;
    // The poll builds each state afresh. A stop already asked for stays on
    // screen until the batch ends, or the strip would flip back to "uploading".
    if (prev?.stopping && prev.eventId === next.eventId
      && (next.phase === 'uploading' || next.phase === 'processing')) {
      next = { ...next, stopping: true };
    }
    // Guards against a change-detection loop on OnPush hosts and stops the
    // banner re-animating on every poll that changed nothing.
    if (prev
      && prev.uploaded === next.uploaded
      && prev.completed === next.completed
      && prev.failed === next.failed
      && prev.remaining === next.remaining
      && prev.phase === next.phase) {
      return;
    }
    this.state.next(next);
  }

  /**
   * Largest batch size any field in the snapshot can vouch for. `filesCount`
   * alone is not trustworthy: it is written from `selectedFiles.length`, which
   * a failed restore can leave empty.
   */
  private resolveTotal(stored: StoredProcessingState): number {
    return Math.max(
      stored.filesCount || 0,
      stored.s3Keys?.length || 0,
      (stored.completed || 0) + (stored.failed || 0) + (stored.remaining || 0)
    );
  }

  private toPercent(completed: number, failed: number, total: number): number {
    if (!total) {
      return 0;
    }
    return Math.min(Math.round(((completed + failed) / total) * 100), 100);
  }

  private stopTimers(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.doneTimer) {
      clearTimeout(this.doneTimer);
      this.doneTimer = null;
    }
  }
}
